import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, ShieldCheck, Wrench, Truck, Award } from "lucide-react";

const BRANDS = [
  { name: "Admetec", tag: "Israel · Loupes & Imaging" },
  { name: "Medesy", tag: "Italy · Surgical Instruments" },
  { name: "Salli", tag: "Finland · Ergonomic Seating" },
  { name: "Anya", tag: "India · Dental Chairs" },
  { name: "Wisedent", tag: "Korea · Multifunction Units" },
  { name: "Gigaa", tag: "Global · Diode Lasers" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[var(--fm-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 py-20 lg:py-28">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <span className="overline text-[var(--fm-accent)]">Premium dental equipment · India</span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] mt-5">
              The instruments behind <em className="text-[var(--fm-primary)] not-italic">India's most precise</em> dentistry.
            </h1>
            <p className="text-base lg:text-lg text-[var(--fm-text-2)] mt-6 max-w-xl leading-relaxed">
              White Molar is the curated destination for Admetec loupes, Medesy surgical instruments, Salli ergonomic seating, Anya dental chairs, Wisedent 3-in-1 units and Gigaa lasers — delivered across India with white-glove installation.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/products" data-testid="hero-shop-cta" className="fm-btn-primary inline-flex items-center gap-2">
                Browse the catalogue <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <Link to="/contact" data-testid="hero-quote-cta" className="fm-btn-secondary inline-flex items-center gap-2">
                Request a bulk quote
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[
                { k: "6", v: "Premium brands" },
                { k: "120+", v: "Indian clinics" },
                { k: "48h", v: "Metro delivery" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl text-[var(--fm-primary)]">{s.k}</div>
                  <div className="overline mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden bg-[var(--fm-sage)]">
              <img
                src="https://images.pexels.com/photos/5355921/pexels-photo-5355921.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Modern dental clinic"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur p-5 border border-[var(--fm-border)]">
                <div className="overline text-[var(--fm-accent)]">Editor's pick</div>
                <div className="font-display text-xl mt-1">Admetec Loupes + ProLight</div>
                <div className="text-xs text-[var(--fm-text-2)] mt-1">The benchmark in dental magnification.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promises */}
      <section className="border-b border-[var(--fm-border)] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { Icon: ShieldCheck, t: "Authentic & warrantied", d: "Direct from authorized distributors." },
            { Icon: Truck, t: "Pan-India delivery", d: "Insured shipping & installation." },
            { Icon: Wrench, t: "On-site servicing", d: "Engineer-led setup & training." },
            { Icon: Award, t: "GST invoiced", d: "Section 35AD eligible billing." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="flex gap-4 items-start">
              <Icon size={24} strokeWidth={1.5} className="text-[var(--fm-primary)] mt-1" />
              <div>
                <div className="font-medium text-sm">{t}</div>
                <div className="text-xs text-[var(--fm-text-2)] mt-1">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Brands grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">Our houses</div>
            <h2 className="font-display text-4xl tracking-tight mt-2">Six brands, one standard.</h2>
          </div>
          <Link to="/brands" className="text-sm text-[var(--fm-primary)] underline underline-offset-4">All brands</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[var(--fm-border)] border border-[var(--fm-border)]">
          {BRANDS.map((b) => (
            <Link
              key={b.name}
              to={`/products?brand=${encodeURIComponent(b.name)}`}
              data-testid={`brand-tile-${b.name.toLowerCase()}`}
              className="bg-white p-8 hover:bg-[var(--fm-sage)] transition-colors group"
            >
              <div className="font-display text-3xl tracking-tight">{b.name}</div>
              <div className="overline mt-3">{b.tag}</div>
              <div className="mt-8 inline-flex items-center gap-2 text-xs text-[var(--fm-primary)]">
                Explore <ArrowRight size={14} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="border-t border-[var(--fm-border)] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline">Featured</div>
              <h2 className="font-display text-4xl tracking-tight mt-2">Hand-picked for working clinicians.</h2>
            </div>
            <Link to="/products" className="text-sm text-[var(--fm-primary)] underline underline-offset-4">View all</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--fm-primary)] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-[var(--fm-accent)]">Setting up a new operatory?</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-3 tracking-tight">Get a clinic-wide proposal in 24 hours.</h2>
            <p className="text-white/80 mt-4 max-w-xl">Tell us about your practice — we will assemble a quotation across chairs, instruments, magnification and seating, with finance options.</p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end gap-3 flex-wrap">
            <Link to="/contact" data-testid="cta-quote" className="fm-btn-accent">Request a quote</Link>
            <Link to="/products" className="fm-btn-secondary !border-white !text-white hover:!bg-white/10">View catalogue</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
